import React, { useEffect, useRef } from 'react'
import * as THREE from 'three'
import './SiteScene.css'
import { useRetroMode } from '../../context/RetroModeContext'

const CAMERA_FOV = 60
const CAMERA_Z = 15

// No more windows in the background  just the atoms code is actually made
// of: brackets, operators, a handful of keywords. Rendered as individual
// drifting glyphs instead of panels of readable text, so the field reads as
// ambient "code dust" rather than a second thing competing to be read next
// to the foreground IDE windows.
const TOKENS = [
  { t: '{ }', cls: 'default' },
  { t: '( )', cls: 'default' },
  { t: '[ ]', cls: 'default' },
  { t: '=>', cls: 'kw' },
  { t: '</>', cls: 'tag' },
  { t: 'const', cls: 'kw' },
  { t: 'let', cls: 'kw' },
  { t: 'return', cls: 'kw' },
  { t: 'async', cls: 'kw' },
  { t: 'await', cls: 'kw' },
  { t: '...rest', cls: 'default' },
  { t: '//', cls: 'comment' },
  { t: '&&', cls: 'default' },
  { t: '?.', cls: 'default' },
  { t: 'null', cls: 'str' },
  { t: 'true', cls: 'str' },
  { t: '01', cls: 'comment' },
  { t: ';', cls: 'default' },
]

const TOKEN_COLOR = {
  kw: '#ff7a45',
  tag: '#5eead4',
  str: '#5eead4',
  comment: '#7c8397',
  default: '#c7cbdb',
}

const createGlyphTexture = (token) => {
  const canvas = document.createElement('canvas')
  canvas.width = 220
  canvas.height = 100
  const ctx = canvas.getContext('2d')
  ctx.font = '600 42px "Fira Code", Consolas, Menlo, monospace'
  ctx.fillStyle = TOKEN_COLOR[token.cls]
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(token.t, canvas.width / 2, canvas.height / 2 + 2)
  const texture = new THREE.CanvasTexture(canvas)
  texture.needsUpdate = true
  return texture
}

// Same rectangle the old code panels used to spread across, so the field
// still reaches the edges of a widescreen desktop instead of clustering in
// the middle, and stays proportionally full on a narrow phone.
const randomSpread = (aspect) => {
  const visibleHeight =
    2 * CAMERA_Z * Math.tan((CAMERA_FOV * Math.PI) / 180 / 2)
  const visibleWidth = visibleHeight * aspect
  return [
    (Math.random() - 0.5) * visibleWidth * 0.95,
    (Math.random() - 0.5) * visibleHeight * 0.95,
    (Math.random() - 0.5) * 10 - 2,
  ]
}

// A quiet constellation of lines between nearby glyphs  like an import
// graph you can half-see, reinforcing "this is a codebase" without adding
// anything that needs to be read.
const LINK_DISTANCE = 3.4
const buildLinks = (particles) => {
  const pairs = []
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dx = particles[i].baseX - particles[j].baseX
      const dy = particles[i].baseY - particles[j].baseY
      const dz = particles[i].baseZ - particles[j].baseZ
      if (Math.sqrt(dx * dx + dy * dy + dz * dz) < LINK_DISTANCE) {
        pairs.push([i, j])
      }
    }
  }
  return pairs
}

const SiteScene = () => {
  const containerRef = useRef(null)
  const { retroMode } = useRetroMode()

  useEffect(() => {
    const container = containerRef.current
    if (!container || retroMode) return undefined

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches
    const particleCount = isCoarsePointer ? 42 : 72
    const pixelRatioCap = isCoarsePointer ? 1.5 : 2

    const aspect = container.clientWidth / container.clientHeight
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(CAMERA_FOV, aspect, 0.1, 100)
    camera.position.z = CAMERA_Z

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, pixelRatioCap))
    renderer.setSize(container.clientWidth, container.clientHeight)
    container.appendChild(renderer.domElement)

    const particles = []
    for (let i = 0; i < particleCount; i++) {
      const token = TOKENS[i % TOKENS.length]
      const texture = createGlyphTexture(token)
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
      })
      const sprite = new THREE.Sprite(material)
      const [x, y, z] = randomSpread(aspect)
      sprite.position.set(x, y, z)
      // Depth cue: nearer glyphs (larger z) render bigger and brighter,
      // farther ones smaller and dimmer, instead of every glyph reading at
      // the same flat weight.
      const depthT = (z + 12) / 20
      const scale = 0.55 + depthT * 0.9
      sprite.scale.set(scale * 1.7, scale, 1)
      material.opacity = 0.25 + depthT * 0.55
      scene.add(sprite)

      particles.push({
        sprite,
        material,
        texture,
        baseX: x,
        baseY: y,
        baseZ: z,
        baseOpacity: material.opacity,
        phase: Math.random() * Math.PI * 2,
        speed: 0.08 + Math.random() * 0.1,
        driftX: 0.4 + Math.random() * 0.5,
        driftY: 0.3 + Math.random() * 0.4,
        spin: (Math.random() - 0.5) * 0.15,
        yVelocity: 0,
        yOffset: 0,
        parallaxStrength: 0.0006 + Math.random() * 0.001,
      })
    }

    const linkPairs = isCoarsePointer ? [] : buildLinks(particles)
    const linkGeometry = new THREE.BufferGeometry()
    const linkPositions = new Float32Array(linkPairs.length * 6)
    linkGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(linkPositions, 3).setUsage(
        THREE.DynamicDrawUsage,
      ),
    )
    const linkMaterial = new THREE.LineBasicMaterial({
      color: 0x7c8397,
      transparent: true,
      opacity: 0.16,
    })
    const linkLines = new THREE.LineSegments(linkGeometry, linkMaterial)
    if (linkPairs.length) scene.add(linkLines)

    const mouse = { x: 0, y: 0 }
    let frameId
    let running = false
    let clock = 0
    let lastScrollY = window.scrollY

    const renderFrame = () => {
      clock += 0.016
      const scrollY = window.scrollY
      const scrollDelta = scrollY - lastScrollY
      lastScrollY = scrollY

      particles.forEach((p) => {
        const t = clock * p.speed + p.phase
        p.yVelocity = (p.yVelocity - scrollDelta * p.parallaxStrength) * 0.92
        p.yOffset = (p.yOffset + p.yVelocity) * 0.97

        p.sprite.position.x = p.baseX + Math.sin(t) * p.driftX
        p.sprite.position.y =
          p.baseY + Math.cos(t * 0.85) * p.driftY + p.yOffset
        p.material.rotation += p.spin * 0.02
        // A slow, gentle twinkle so the field feels alive even when the
        // page itself is still.
        p.material.opacity =
          p.baseOpacity * (0.75 + 0.25 * Math.sin(t * 1.4))
      })

      if (linkPairs.length) {
        const pos = linkGeometry.attributes.position.array
        linkPairs.forEach(([a, b], idx) => {
          const pa = particles[a].sprite.position
          const pb = particles[b].sprite.position
          const o = idx * 6
          pos[o] = pa.x
          pos[o + 1] = pa.y
          pos[o + 2] = pa.z
          pos[o + 3] = pb.x
          pos[o + 4] = pb.y
          pos[o + 5] = pb.z
        })
        linkGeometry.attributes.position.needsUpdate = true
      }

      if (!isCoarsePointer) {
        camera.position.x += (mouse.x * 1.2 - camera.position.x) * 0.02
        camera.position.y += (-mouse.y * 1.2 - camera.position.y) * 0.02
        camera.lookAt(scene.position)
      }
      renderer.render(scene, camera)

      if (running) frameId = requestAnimationFrame(renderFrame)
    }

    const startLoop = () => {
      if (running) return
      running = true
      renderFrame()
    }
    const stopLoop = () => {
      running = false
      cancelAnimationFrame(frameId)
    }

    const handleMouseMove = (event) => {
      mouse.x = (event.clientX / window.innerWidth - 0.5) * 2
      mouse.y = (event.clientY / window.innerHeight - 0.5) * 2
    }

    const handleVisibilityChange = () => {
      if (document.hidden) stopLoop()
      else if (!reduceMotion) startLoop()
    }

    const handleResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight
      camera.updateProjectionMatrix()
      renderer.setSize(container.clientWidth, container.clientHeight)
    }

    renderer.render(scene, camera)
    if (reduceMotion) {
      // Static frame, no drift/twinkle/parallax.
    } else {
      startLoop()
      if (!isCoarsePointer) {
        window.addEventListener('mousemove', handleMouseMove)
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      stopLoop()
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      linkGeometry.dispose()
      linkMaterial.dispose()
      particles.forEach((p) => {
        p.material.dispose()
        p.texture.dispose()
      })
      renderer.dispose()
      container.removeChild(renderer.domElement)
    }
  }, [retroMode])

  if (retroMode) return null

  return <div className="site-scene" ref={containerRef} aria-hidden="true" />
}

export default SiteScene
